/**
 * Shared image compression service
 * Provides centralized compression logic with server-side processing and client-side fallback
 */

import imageCompression from "browser-image-compression";
import { getCompressionUrl } from "../config/api";
import { CompressionResult } from "../types/compression";
import { compressionLogger } from "../utils/compressionLogger";

// Client-side compression configuration
const CLIENT_COMPRESSION_CONFIG = {
  maxSizeMB: 2,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
} as const;

/**
 * Helper: Read file as data URL
 */
function readFileAsDataURL(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Compress image using server-side Sharp compression
 * @param file - Image file to compress
 * @returns Compression result with compressed image and statistics
 * @throws Error if server compression fails
 */
export async function compressImageServerSide(
  file: File
): Promise<CompressionResult> {
  const formData = new FormData();
  formData.append("image", file);

  const response = await fetch(getCompressionUrl(), {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(`Server compression failed: ${response.statusText}`);
  }

  const result: CompressionResult = await response.json();

  // Log success with statistics
  compressionLogger.success(
    result.originalSize,
    result.compressedSize,
    result.format,
    { width: result.width, height: result.height }
  );

  return result;
}

/**
 * Compress image using client-side browser compression
 * @param file - Image file to compress
 * @returns Base64 data URI of compressed image
 */
export async function compressImageClientSide(file: File): Promise<string> {
  const originalSize = file.size;

  const compressedFile = await imageCompression(file, CLIENT_COMPRESSION_CONFIG);

  compressionLogger.clientSide(originalSize, compressedFile.size);

  return await readFileAsDataURL(compressedFile);
}

/**
 * Compress image with automatic fallback
 * Tries server-side compression first, falls back to client-side if it fails
 * @param file - Image file to compress
 * @returns Base64 data URI of compressed image
 */
export async function compressImageWithFallback(file: File): Promise<string> {
  try {
    const result = await compressImageServerSide(file);
    return result.data;
  } catch (error) {
    compressionLogger.fallback(error);
    return await compressImageClientSide(file);
  }
}
